import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";
import { Plus, Check, ChevronRight, Pencil, Search, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listAllAllotmentHistory, markAssetReturned, deleteAssetAllotment, deleteEmployeeAllotments,
  type AllotmentHistoryRowWithEmployee,
} from "@/lib/hr";
import { NewAllotmentModal } from "@/components/hr/NewAllotmentModal";
import { EditAssetModal } from "@/components/hr/EditAssetModal";
import { StatusPill } from "@/components/ui/status-pill";
import { cn } from "@/lib/utils";
import { todayIstIso, formatIst } from "@/lib/tz";

export const Route = createFileRoute("/_app/_hr/hr/assets")({
  component: AssetsPage,
  head: () => ({
    meta: [{ title: "Allot Device / Locker / Assets — HR" }],
  }),
});

interface Group {
  employee_id: string;
  employee_name: string;
  employee_emp_id: string | null;
  employee_department: string | null;
  locker: string | null;
  assets: AllotmentHistoryRowWithEmployee[];
}

function AssetsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [prefillEmp, setPrefillEmp] = useState<string | null>(null);
  const [editAsset, setEditAsset] = useState<AllotmentHistoryRowWithEmployee | null>(null);
  const [deleteAsset, setDeleteAsset] = useState<AllotmentHistoryRowWithEmployee | null>(null);
  const [deleteEmp, setDeleteEmp] = useState<Group | null>(null);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data: history = [], isLoading } = useQuery({
    queryKey: ["allotment-history-all"],
    queryFn: listAllAllotmentHistory,
  });

  const returnMut = useMutation({
    mutationFn: ({ id, returned_at }: { id: string; returned_at: string | null }) =>
      markAssetReturned(id, returned_at),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["allotment-history-all"] }),
  });

  const deleteAssetMut = useMutation({
    mutationFn: (id: string) => deleteAssetAllotment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["allotment-history-all"] });
      toast.success("Asset deleted");
      setDeleteAsset(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteEmpMut = useMutation({
    mutationFn: (employeeId: string) => deleteEmployeeAllotments(employeeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["allotment-history-all"] });
      toast.success("All allotments removed for employee");
      setDeleteEmp(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const groups: Group[] = useMemo(() => {
    const map = new Map<string, Group>();
    for (const h of history) {
      let g = map.get(h.employee_id);
      if (!g) {
        g = {
          employee_id: h.employee_id,
          employee_name: h.employee_name,
          employee_emp_id: h.employee_emp_id,
          employee_department: h.employee_department,
          locker: h.locker_number,
          assets: [],
        };
        map.set(h.employee_id, g);
      }
      if (!g.locker && h.locker_number) g.locker = h.locker_number;
      g.assets.push(h);
    }
    return Array.from(map.values()).sort((a, b) => a.employee_name.localeCompare(b.employee_name));
  }, [history]);

  const s = search.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!s) return groups;
    return groups
      .map((g) => {
        const empMatch =
          g.employee_name.toLowerCase().includes(s) ||
          (g.employee_emp_id ?? "").toLowerCase().includes(s);
        const matchedAssets = g.assets.filter(
          (a) =>
            a.asset_name.toLowerCase().includes(s) ||
            (a.serial_number ?? "").toLowerCase().includes(s) ||
            a.asset_type.toLowerCase().includes(s),
        );
        if (empMatch) return g;
        if (matchedAssets.length) return { ...g, assets: matchedAssets };
        return null;
      })
      .filter((g): g is Group => g !== null);
  }, [groups, s]);

  const isExpanded = (id: string) => (s ? true : expanded.has(id));
  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const openAdd = (employeeId: string | null) => {
    setPrefillEmp(employeeId);
    setOpen(true);
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Allot Device / Locker / Assets</h1>
          <p className="text-xs text-muted-foreground">Grouped by employee. Click a row to expand.</p>
        </div>
        <Button onClick={() => openAdd(null)}>
          <Plus className="mr-1 h-4 w-4" /> New Allotment
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search employee, asset name, serial…"
          className="h-9 pl-7 text-xs"
        />
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="grid grid-cols-[24px_2fr_1fr_1fr_100px_120px_140px] items-center gap-3 border-b bg-muted/40 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <span />
          <span>Employee</span>
          <span>Dept</span>
          <span>Locker</span>
          <span className="text-center">Assets</span>
          <span>Last Updated</span>
          <span className="text-right">Actions</span>
        </div>

        {isLoading && (
          <div className="px-4 py-6 text-center text-xs text-muted-foreground">Loading…</div>
        )}
        {!isLoading && filteredGroups.length === 0 && (
          <div className="px-4 py-6 text-center text-xs text-muted-foreground">
            No allotments yet. Click + New Allotment.
          </div>
        )}

        {filteredGroups.map((g) => {
          const expandedNow = isExpanded(g.employee_id);
          const activeCount = g.assets.filter((a) => !a.returned_at).length;
          const lastUpdated = g.assets
            .map((a) => a.allotment_date)
            .sort()
            .reverse()[0];

          return (
            <div key={g.employee_id} className="border-b last:border-b-0">
              <div
                role="button"
                onClick={() => toggle(g.employee_id)}
                className="grid grid-cols-[24px_2fr_1fr_1fr_100px_120px_140px] items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/40 transition-colors"
              >
                <ChevronRight
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform",
                    expandedNow && "rotate-90",
                  )}
                />
                <div>
                  <div className="text-sm font-semibold">{g.employee_name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    #{g.employee_emp_id ?? "—"}
                  </div>
                </div>
                <div className="text-xs">{g.employee_department ?? "—"}</div>
                <div className="text-xs font-mono">{g.locker ?? "—"}</div>
                <div className="text-center text-xs font-medium">{activeCount}</div>
                <div className="text-xs text-muted-foreground">
                  {lastUpdated ? formatIst(lastUpdated, "dd MMM yyyy") : "—"}
                </div>
                <div className="text-right flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[10px]"
                    onClick={() => openAdd(g.employee_id)}
                  >
                    <Plus className="mr-1 h-3 w-3" /> Add Asset
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                    onClick={() => setDeleteEmp(g)}
                    title="Delete all allotments for this employee"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {expandedNow && (
                <div className="bg-muted/30 border-l-[3px] border-violet-600">
                  <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_120px_140px] gap-3 border-b px-4 py-2 pl-12 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    <span>Asset Name</span>
                    <span>Type</span>
                    <span>Date</span>
                    <span>Condition</span>
                    <span>Serial</span>
                    <span>Status</span>
                    <span className="text-right">Actions</span>
                  </div>
                  {g.assets.map((a) => (
                    <div
                      key={a.id}
                      className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_120px_140px] gap-3 border-b last:border-b-0 px-4 py-2 pl-12 text-[13px]"
                    >
                      <div className="font-medium">{a.asset_name}</div>
                      <div>{a.asset_type}</div>
                      <div className="text-muted-foreground">
                        {formatIst(a.allotment_date, "dd MMM yyyy")}
                      </div>
                      <div className="capitalize">{a.condition}</div>
                      <div className="font-mono text-xs">{a.serial_number || "—"}</div>
                      <div>
                        {a.returned_at ? (
                          <StatusPill status="returned" label="Returned" />
                        ) : (
                          <StatusPill status="active" label="Active" />
                        )}
                      </div>
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditAsset(a)}>
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                          onClick={() => setDeleteAsset(a)}
                          title="Delete asset"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                        {a.returned_at ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-[10px]"
                            onClick={() => returnMut.mutate({ id: a.id, returned_at: null })}
                          >
                            Undo
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[10px]"
                            onClick={() =>
                              returnMut.mutate({
                                id: a.id,
                                returned_at: todayIstIso(),
                              })
                            }
                          >
                            <Check className="mr-1 h-3 w-3" /> Return
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <NewAllotmentModal
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setPrefillEmp(null);
        }}
        lockedEmployeeId={prefillEmp}
      />
      <EditAssetModal
        asset={editAsset}
        open={!!editAsset}
        onOpenChange={(v) => { if (!v) setEditAsset(null); }}
      />

      <AlertDialog open={!!deleteAsset} onOpenChange={(v) => { if (!v) setDeleteAsset(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteAsset && (
                <>
                  <span className="font-medium">{deleteAsset.asset_name}</span> ({deleteAsset.asset_type})
                  {" "}assigned to <span className="font-medium">{deleteAsset.employee_name}</span> will be permanently removed.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteAsset && deleteAssetMut.mutate(deleteAsset.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteEmp} onOpenChange={(v) => { if (!v) setDeleteEmp(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete entire allotment entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteEmp && (
                <>
                  This will permanently remove <span className="font-medium">{deleteEmp.assets.length} asset(s)</span> and the locker assignment for <span className="font-medium">{deleteEmp.employee_name}</span>. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteEmp && deleteEmpMut.mutate(deleteEmp.employee_id)}
            >
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
